import { Button, Flex } from '@mantine/core';
import { ConfirmationModalV3 } from '@dealstack/frontend/shared/components/ConfirmationModal/ConfirmationModalV3.tsx';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import type { InterestPairEditPlan } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/hooks/interestPairEdit.utils';

interface Props {
	plan: InterestPairEditPlan;
	onProceed: () => void;
	onClose: () => void;
	// Required, not optional. Every blocked plan that offers it tells the user to delete the pair,
	// so a call site that forgets to pass it leaves a dialog whose only instruction has no button —
	// which is exactly what happened while this was optional. Hands off to the caller's own delete
	// confirmation; the delete itself cascades to both legs server-side.
	onDeletePair: () => void;
}

export const InterestPairEditModal = ({ plan, onProceed, onClose, onDeletePair }: Props) => {
	const tCapManage = useTranslationCapManage();

	if (plan.kind === 'blocked') {
		return (
			<ConfirmationModalV3
				title={plan.title}
				description={plan.message}
				state="warning"
				children={
					<Flex gap="xs">
						{plan.offerDeletePair ? (
							<Button
								size="md"
								variant="secondary"
								data-testid="interest-pair-delete-pair-button"
								onClick={() => {
									onClose();
									onDeletePair();
								}}
							>
								{tCapManage('transactions.interest_pair.modal.delete_pair')}
							</Button>
						) : null}
						<Button size="md" variant="subtle" onClick={onClose}>
							{tCapManage('transactions.interest_pair.modal.close')}
						</Button>
					</Flex>
				}
			/>
		);
	}

	const getProceedLabel = () => {
		if (plan.kind === 'confirm') {
			return tCapManage('transactions.interest_pair.modal.update_both');
		}
		if (plan.kind === 'splitPreview') {
			return tCapManage('transactions.interest_pair.modal.create_leg');
		}
		if (plan.kind === 'mergePreview') {
			return tCapManage('transactions.interest_pair.modal.merge_pair');
		}
		return tCapManage('transactions.interest_pair.modal.proceed');
	};
	const proceedLabel = getProceedLabel();

	return (
		<ConfirmationModalV3
			title={plan.title}
			description={plan.message}
			state="info"
			children={
				<Flex gap="xs">
					<Button size="md" variant="secondary" onClick={onClose}>
						{tCapManage('transactions.interest_pair.modal.cancel')}
					</Button>
					<Button
						size="md"
						variant="subtle"
						data-testid="interest-pair-proceed-button"
						onClick={() => {
							// Fire the mutation BEFORE closing. Closing first unmounts the drawer that
							// owns the mutation's callbacks, so a rejection had nowhere to report to and
							// the user saw a modal vanish and nothing else.
							onProceed();
							onClose();
						}}
					>
						{proceedLabel}
					</Button>
				</Flex>
			}
		/>
	);
};
